/**

=========================================================
* Vision UI PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/

// @mui material components
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import type { CSSObject } from "@mui/material/styles";

interface OwnerState {
  color:
    | "inherit"
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "light"
    | "dark"
    | "text"
    | "white"
    // Only a gradient (gradients.logo), for textGradient; the plain color is inherited.
    | "logo";
  fontWeight: false | "light" | "regular" | "medium" | "bold";
  textTransform: "none" | "capitalize" | "uppercase" | "lowercase";
  verticalAlign:
    | "unset"
    | "baseline"
    | "sub"
    | "super"
    | "text-top"
    | "text-bottom"
    | "middle"
    | "top"
    | "bottom";
  opacity: number;
  textGradient: boolean;
}

export default styled(Typography)<{ ownerState: OwnerState }>(({ theme, ownerState }) => {
  const { palette, typography, functions } = theme;
  const { color, textTransform, verticalAlign, fontWeight, opacity, textGradient } = ownerState;

  const { gradients, transparent } = palette;
  const { fontWeightLight, fontWeightRegular, fontWeightMedium, fontWeightBold } = typography;
  const { linearGradient } = functions;

  // Every palette color named above holds a `main` (the Vision colors add one to MUI's `text` too).
  const paletteColors = palette as unknown as Record<string, { main: string } | undefined>;

  // fontWeight styles
  const fontWeights = {
    light: fontWeightLight,
    regular: fontWeightRegular,
    medium: fontWeightMedium,
    bold: fontWeightBold,
  };

  // styles for the typography with textGradient={true}
  const gradientStyles = (): CSSObject => {
    const gradient: { main: string; state: string; deg?: string } | undefined =
      color !== "inherit" && color !== "text" && color !== "white" ? gradients[color] : undefined;

    return {
      backgroundImage: gradient
        ? linearGradient(gradient.main, gradient.state, gradient.deg)
        : linearGradient(gradients.primary.main, gradients.primary.state),
      display: "inline-block",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: transparent.main,
      position: "relative",
      zIndex: 1,
    };
  };

  const paletteColor = paletteColors[color];

  return {
    opacity,
    textTransform,
    verticalAlign,
    textDecoration: "none",
    color: color === "inherit" || !paletteColor ? "inherit" : paletteColor.main,
    fontWeight: fontWeight ? fontWeights[fontWeight] : undefined,
    ...(textGradient && gradientStyles()),
  };
});
